import { Handle, type Node, Position } from "@xyflow/react";
import { clsx } from "clsx";
import { map, max } from "ramda";
import { type CSSProperties, type FC, memo } from "react";
import { FormattedMessage } from "react-intl";
import { Badge, Center, HStack, Spacer, VStack } from "widgets";

import { WorkflowActivityStatusEnum } from "@/graphql.ts";
import type { PreviewNodeData } from "@/modules/workflow/utils/types.ts";

import style from "../styles/ActivityNode.module.css";

export const WorkflowPreviewNode: FC<Node<PreviewNodeData>> = memo(function WorkflowPreviewNode(props) {
  const { activity, status, width, staged: hasStagedParameters, disabled } = props.data;

  if (!activity)
    return (
      <div className={style.io}>
        <FormattedMessage id="an error occurred" />
      </div>
    );

  return (
    <VStack
      spacing={2}
      className={style.node}
      style={{ "--width": `${max(0, width || 150)}px` } as CSSProperties}
      data-key={props.id}
      data-name={activity.name}
      data-version={activity.version}
    >
      <VStack spacing={0} style={{ overflow: "hidden" }}>
        <div className={clsx(style.name, { [style.name_selected]: props.selected })}>{activity.label}</div>
      </VStack>
      <VStack
        spacing={3}
        className={clsx(style.io, {
          [style.io_selected]: props.selected,
          [style.io_running]: status === WorkflowActivityStatusEnum.Running,
          [style.io_success]: status === WorkflowActivityStatusEnum.Completed,
          [style.io_run_error]: status === WorkflowActivityStatusEnum.Error,
          [style.io_terminated]: status === WorkflowActivityStatusEnum.Terminated,
          [style.node_disabled]: disabled,
        })}
        data-activity-disabled={disabled}
        data-testid="io"
        data-run-status={status}
      >
        {map(
          (input) => (
            <Input id={input.id} name={input.label} key={input.id} />
          ),
          activity.inputs ?? [],
        )}
        {map(
          (output) => (
            <Output id={output.id} name={output.label} key={output.id} />
          ),
          activity.outputs ?? [],
        )}
      </VStack>
      <HStack spacing={1} data-testid="footer">
        <Spacer />
        {hasStagedParameters && (
          <Center height={18} width={18}>
            <Badge label="P" inline />
          </Center>
        )}
      </HStack>
    </VStack>
  );
});

interface IOProps {
  id: string; // sourceHandle
  name: string;
}

const Input: FC<IOProps> = (props) => {
  return (
    <HStack>
      <Handle type="target" id={props.id} position={Position.Left} className={clsx(style.handle)} />
      <div>{props.name}</div>
    </HStack>
  );
};

const Output: FC<IOProps> = (props) => {
  return (
    <HStack style={{ justifyContent: "flex-end" }}>
      <div className={style.output_right_align}>{props.name}</div>
      <Handle type="source" id={props.id} position={Position.Right} className={clsx(style.handle)} />
    </HStack>
  );
};
